import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  FileSearch,
  ShieldAlert,
  UserCheck,
  X,
} from "lucide-react";
import { toast } from "sonner";
import {
  draftFromTop5,
  nextProblemRecordId,
  saveProblemRecord,
  top5SourceLabel,
  PROTOTYPE_TODAY,
} from "@/lib/problem-record-store";
import { claimsForItem, currency, type Top5Item } from "@/lib/top5-data";
import type { ProblemRecord } from "@/lib/problem-record-data";

const OWNERS = [
  "M. Chen — Quality Engineering",
  "A. Ricci — Powertrain Quality",
  "J. Novak — Chassis Quality",
  "S. Idris — Electronics Quality",
  "K. Okafor — Supplier Quality",
  "L. García — Prevention & Read-Across",
];

const SPONSORS = [
  "D. Shumar — VP Quality",
  "P. Andersson — Director Engineering",
  "R. Patel — Director Service Operations",
  "—",
];

const SEVERITIES: ProblemRecord["severity"][] = ["Critical", "High", "Medium", "Low"];

function initialRisk(item: Top5Item): number {
  const trendBoost = item.trend === "Rising" ? 8 : item.trend === "Falling" ? -6 : 0;
  const repeatBoost = item.repeatOccurrence ? 5 : 0;
  return Math.max(
    1,
    Math.min(
      100,
      Math.round(item.severityScore * 7 + item.customerImpact * 0.25 + trendBoost + repeatBoost),
    ),
  );
}

export function CreateProblemRecordDialog({
  item,
  view,
  onClose,
}: {
  item: Top5Item;
  view: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const claims = useMemo(() => claimsForItem(item), [item]);
  const [step, setStep] = useState<1 | 2>(1);
  const [recordId] = useState(() => nextProblemRecordId());
  const [title, setTitle] = useState(`${item.name} — ${item.component} / ${item.carLine}`);
  const [owner, setOwner] = useState(OWNERS[0]);
  const [sponsor, setSponsor] = useState(SPONSORS[0]);
  const [severity, setSeverity] = useState<ProblemRecord["severity"]>(item.severity);
  const [risk, setRisk] = useState(() => initialRisk(item));
  const [statement, setStatement] = useState(
    `${item.description} ${item.claims.toLocaleString()} warranty claims and ${currency(item.cost)} exposure recorded on ${item.component} (${item.supplier}) across ${item.carLine}${item.ratePer1000 != null ? ` at ${item.ratePer1000.toFixed(1)} claims / 1,000` : ""}.`,
  );
  const [start8D, setStart8D] = useState(true);
  const [creating, setCreating] = useState(false);

  function handleCreate() {
    setCreating(true);
    const record = draftFromTop5(item, {
      id: recordId,
      title: title.trim() || item.name,
      owner,
      sponsor: sponsor === "—" ? "" : sponsor,
      severity,
      riskScore: risk,
      problemStatement: statement.trim(),
      start8D,
    });
    saveProblemRecord(record);
    toast.success(`Problem Record ${recordId} created`, {
      description: start8D
        ? "Warranty signal captured · 8D initiated at D1/D2"
        : "Warranty signal captured · awaiting problem definition",
    });
    navigate({ to: "/problem-record/$id", params: { id: recordId } });
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-background/80 p-4 backdrop-blur-sm sm:p-8">
      <div className="w-full max-w-4xl rounded-xl border border-border bg-card shadow-2xl">
        <div className="flex items-start gap-3 border-b border-border px-6 py-5">
          <div
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
            style={{ background: "var(--gradient-primary)" }}
          >
            <ShieldAlert className="h-5 w-5 text-primary-foreground" />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Quality escalation · Step {step} of 2
            </div>
            <h2 className="mt-0.5 text-lg font-semibold">
              Create Problem Record <span className="text-primary">{recordId}</span>
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Escalating <strong className="text-foreground">{item.name}</strong> from {view}.
              Broadview carries all known warranty evidence forward — you confirm ownership and
              escalation.
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-auto rounded-md border border-border p-1.5 text-muted-foreground hover:bg-muted"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {step === 1 ? (
          <div className="space-y-5 px-6 py-6">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <FileSearch className="h-4 w-4 text-primary" /> Evidence Broadview will carry forward
            </div>

            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {[
                { l: "Claims", v: item.claims.toLocaleString() },
                { l: "Warranty exposure", v: currency(item.cost) },
                {
                  l: "Claims / 1,000",
                  v: item.ratePer1000 != null ? item.ratePer1000.toFixed(1) : "—",
                },
                { l: "Severity signal", v: `${item.severity} · Sev ${item.severityScore}` },
              ].map((k) => (
                <div key={k.l} className="rounded-lg border border-border bg-background p-3">
                  <div className="text-[11px] text-muted-foreground">{k.l}</div>
                  <div className="mt-0.5 text-base font-semibold">{k.v}</div>
                </div>
              ))}
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <dl className="rounded-lg border border-border bg-background p-4 text-xs">
                <div className="mb-2 text-sm font-semibold">Attribution</div>
                <Line l="Source" v={`${view} · ${item.name}`} />
                <Line l="Component" v={item.component} />
                <Line l="Supplier" v={item.supplier} />
                <Line l="Car line" v={item.carLine} />
                <Line l="Failure mode" v={item.failureMode} />
                <Line l="Linked 8D" v={item.linkedCaseId ?? "None"} />
              </dl>
              <dl className="rounded-lg border border-border bg-background p-4 text-xs">
                <div className="mb-2 text-sm font-semibold">Prioritization signals</div>
                <Line
                  l="Trend"
                  v={`${item.trendPct > 0 ? "+" : ""}${item.trendPct.toFixed(1)}% MoM · ${item.trend}`}
                />
                <Line
                  l="Repeat occurrence"
                  v={item.repeatOccurrence ? `Yes — ${item.repeatCount} prior` : "First occurrence"}
                />
                <Line l="Customer impact" v={`${item.customerImpact} / 100`} />
                <Line l="SPC / predictive" v={item.spcSignal ?? "No special-cause signal"} />
                <Line l="Opened on" v={PROTOTYPE_TODAY} />
                <Line l="Initial stage" v="1 · Warranty Signal (Active)" />
              </dl>
            </div>

            <div className="rounded-lg border border-border bg-background p-4">
              <div className="mb-2 text-sm font-semibold">
                Linked claim evidence{" "}
                <span className="text-xs font-normal text-muted-foreground">
                  {claims.length} sample claims · {currency(claims.reduce((s, c) => s + c.cost, 0))}
                </span>
              </div>
              <ul className="space-y-1 text-xs text-muted-foreground">
                {claims.slice(0, 4).map((c) => (
                  <li key={c.id} className="flex flex-wrap gap-2">
                    <span className="font-medium text-foreground">{c.id}</span>
                    <span>{c.date}</span>
                    <span>{c.failureMode}</span>
                    <span className="ml-auto">${c.cost.toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
              <button
                onClick={onClose}
                className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted"
              >
                Cancel
              </button>
              <button
                onClick={() => setStep(2)}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
              >
                Assign ownership <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-5 px-6 py-6">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <UserCheck className="h-4 w-4 text-primary" /> Assign ownership / confirm escalation
            </div>

            <Field label="Problem Record title">
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
              />
            </Field>

            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Owner (problem-solving lead)">
                <select
                  value={owner}
                  onChange={(e) => setOwner(e.target.value)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                >
                  {OWNERS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Sponsor / management owner (optional)">
                <select
                  value={sponsor}
                  onChange={(e) => setSponsor(e.target.value)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                >
                  {SPONSORS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Severity">
                <div className="flex flex-wrap gap-2">
                  {SEVERITIES.map((s) => (
                    <button
                      key={s}
                      onClick={() => setSeverity(s)}
                      className={`rounded-md border px-3 py-1.5 text-xs font-medium ${
                        s === severity
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border text-muted-foreground hover:bg-muted"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label={`Priority / risk score — ${risk}`}>
                <input
                  type="range"
                  min={1}
                  max={100}
                  value={risk}
                  onChange={(e) => setRisk(Number(e.target.value))}
                  className="w-full accent-[hsl(var(--primary))]"
                />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Prefilled from severity, customer impact, trend and recurrence.
                </p>
              </Field>
            </div>

            <Field label="Problem statement / initial definition">
              <textarea
                value={statement}
                onChange={(e) => setStatement(e.target.value)}
                rows={4}
                className="w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
              />
            </Field>

            <Field label="Start an 8D now?">
              <div className="flex gap-2">
                {[
                  { v: true, l: "Yes — initiate 8D (D1/D2 open)" },
                  { v: false, l: "Later — hold at Warranty Signal" },
                ].map((o) => (
                  <button
                    key={String(o.v)}
                    onClick={() => setStart8D(o.v)}
                    className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium ${
                      start8D === o.v
                        ? "border-primary bg-primary/15 text-primary"
                        : "border-border text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    <ClipboardList className="h-3.5 w-3.5" /> {o.l}
                  </button>
                ))}
              </div>
            </Field>

            <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 p-3 text-xs text-warning">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Downstream stages (root cause, corrective action, validation, effectiveness) stay
              pending until real evidence exists. Broadview will not pre-fill them.
            </div>

            <div className="flex items-center justify-between gap-2 border-t border-border pt-4">
              <button
                onClick={() => setStep(1)}
                className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted"
              >
                Back to evidence
              </button>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
              >
                <CheckCircle2 className="h-4 w-4" /> Create {recordId}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Line({ l, v }: { l: string; v: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-border/60 py-1 last:border-0">
      <dt className="text-muted-foreground">{l}</dt>
      <dd className="text-right font-medium">{v}</dd>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

export { top5SourceLabel };
